import React, {lazy, Suspense} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {MAXIMUM_POPULAR_FACILITIES} from 'components/Accommodation/PopularFacilities/constants'
import {useAccommodation} from 'hooks/useAccommodation'
import {useConditionalEffect} from 'hooks/useConditionalEffect'
import {setScrollToApTarget} from 'modules/accommodation/slice'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getHotelFacilitiesVisibility} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {getHotelFacilitiesRanked, TOP_FACILITY_IDS} from 'utils/facilities'

import {Button} from '@daedalus/atlas/Button'
import {PopUp} from '@daedalus/atlas/PopUp'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'

interface Props {
  hotel: ContentHotel | undefined
}

const FacilitiesPopupContent = lazy(
  () =>
    import(
      /* webpackChunkName: "FacilitiesBottomSheetContent" */
      './FacilitiesPopupContent'
    )
)

export const FacilitiesPopup = ({hotel}: Props) => {
  const dispatch = useDispatch()
  const isFacilitiesPopupOpen = useSelector(getHotelFacilitiesVisibility)

  const onClose = () => {
    dispatch(hideOverlay())
  }

  const {groupedFacilities} = useAccommodation(hotel)
  const topFacilities =
    hotel?.facilities &&
    getHotelFacilitiesRanked(
      hotel.facilities,
      TOP_FACILITY_IDS,
      MAXIMUM_POPULAR_FACILITIES
    )

  const handleBottomCTAClick = () => {
    dispatch(hideOverlay())
    dispatch(setScrollToApTarget({target: AccommodationPageNavSections.Rooms}))
  }

  useConditionalEffect(
    async () =>
      import(
        /* webpackChunkName: "FacilitiesBottomSheetContent" */
        /* webpackPrefetch: true */
        './FacilitiesPopupContent'
      )
  )

  return (
    <PopUp
      width={900}
      titleBarProps={{
        centerContent: (
          <FormattedMessageWrapper id="amenities" defaultMessage="Amenities" />
        )
      }}
      isOpen={isFacilitiesPopupOpen}
      closeAction={onClose}
      footer={
        <Button
          size="lg"
          fullWidth
          variant="primary"
          onClick={handleBottomCTAClick}
        >
          <FormattedMessageWrapper id="viewRooms" defaultMessage="View rooms" />
        </Button>
      }
    >
      {isFacilitiesPopupOpen && topFacilities && (
        <Suspense fallback={null}>
          <FacilitiesPopupContent
            groupedFacilities={groupedFacilities}
            topFacilities={topFacilities}
          />
        </Suspense>
      )}
    </PopUp>
  )
}

export default FacilitiesPopup
